import { RequestMeta } from '@givebacks/api';
import { ICause, useCoreApi } from '@givebacks/api/core';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { ICausesDonation, IDonationList } from '../../../types/causes/donor.types';

interface IShowCauseResponse {
  cause: ICause;
}

interface IShowCauseDonations {
  donations: IDonationList[];
  cause: ICausesDonation;
  meta: RequestMeta;
}

export const usePrefetchCause = () => {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();

  const getCause = async ({
    uuid,
    isMemberhub,
    cb,
  }: {
    uuid: string;
    isMemberhub: boolean;
    cb?: (uuid: string) => void;
  }) => {
    const memberhubQuery = isMemberhub ? '?memberhub=true' : '';
    const { data } = await CoreApi.get<IShowCauseResponse>(`/causes/${uuid}${memberhubQuery}`);
    const { cause } = data;
    const shortener = cause.shortener;
    if (shortener && uuid == shortener) {
      queryClient.setQueryData(['cause', cause.uuid], cause);
    }
    cb?.(cause.uuid);
    return cause;
  };

  const prefetchCause = async ({
    uuid,
    isMemberhub = false,
    cb,
  }: {
    uuid: string;
    isMemberhub: boolean;
    cb?: (uuid: string) => void;
  }) => {
    return await queryClient.prefetchQuery({
      queryKey: ['cause', uuid],
      queryFn: () => getCause({ uuid, isMemberhub, cb }),
    });
  };
  return prefetchCause;
};

export function useShowCausesDonations(uuid: string | undefined) {
  const CoreApi = useCoreApi();
  const getCauseDonations = async ({
    pageParam,
  }: {
    pageParam: { limit: number; offset: number };
  }) => {
    const { data } = await CoreApi.get<IShowCauseDonations>(
      `/causes/${uuid}/donations?offset=${pageParam.offset}&limit=${pageParam.limit}`
    );
    return data;
  };
  return useInfiniteQuery({
    queryKey: ['cause', uuid, 'donations'],
    queryFn: getCauseDonations,
    initialPageParam: { limit: 10, offset: 0 },
    getNextPageParam: (lastPageData) => {
      const { meta } = lastPageData;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
}
